import { useRef, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { useDialogFocusTrap } from '@/hooks/useDialogFocusTrap';
import { useOverlayEdges } from './overlayEdges';

/**
 * A modal dialog's frame: the backdrop, and a panel that is a dialog to assistive technology and to
 * the keyboard alike — `role="dialog"` (or `alertdialog`), `aria-modal`, a label, focus moved in on
 * open, `Tab` kept inside, `Escape` to close, and focus handed back to whatever opened it
 * (`useDialogFocusTrap`). And no key typed inside reaches the page's own shortcuts behind it.
 *
 * Web QA found the create-work-area dialog announced as nothing more than a form, and the command
 * palette as a region; an audit then found 25 hand-built overlays, one of which used the focus trap.
 * Every modal in the web is built on this frame now, and `modalFrame.guard.test.ts` fails on a
 * `fixed inset-0` dialog built by hand.
 *
 * ⚠️ Render it only while the dialog is open. The focus trap runs once per mount, which is what
 * moves focus in on open and gives it back on close.
 */
export function ModalFrame({
  onClose,
  labelledBy,
  label,
  describedBy,
  role = 'dialog',
  initialFocusRef,
  panelRef: givenPanelRef,
  closeOnBackdrop = true,
  layerClassName = 'z-50 flex items-center justify-center px-4 py-6',
  panelClassName = 'relative max-h-full w-full max-w-md',
  backdropClassName = 'bg-black/45 backdrop-blur-sm',
  belowStatusBar = true,
  layerScrolls = true,
  testId,
  data,
  motion,
  children,
}: {
  onClose: () => void;
  /** The id of the dialog's visible title. Prefer it; `label` is for a dialog with no title. */
  labelledBy?: string;
  label?: string;
  describedBy?: string;
  /** `alertdialog` for a confirmation that interrupts, which a screen reader announces at once. */
  role?: 'dialog' | 'alertdialog';
  /** What takes focus on open, instead of the first control: a confirmation's safe choice. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** The panel element, for a caller that measures or queries it. */
  panelRef?: RefObject<HTMLDivElement | null>;
  /** `false` for a dialog a stray tap beside it must not dismiss — a choice that has to be made. */
  closeOnBackdrop?: boolean;
  /** The fixed layer's own classes: its stacking and where the panel sits. */
  layerClassName?: string;
  panelClassName?: string;
  backdropClassName?: string;
  /**
   * Whether the layer starts below the status bar and ends above the home indicator
   * (`statusBarInset.ts`, I-1163, I-1209). `false` only for a layer that measures both into its own
   * geometry — `Sheet`, whose panel is anchored to
   * the screen's bottom and sized to stop below it.
   */
  belowStatusBar?: boolean;
  /**
   * Whether the layer scrolls a panel taller than it (a tall dialog). `false` for a layer whose
   * panel keeps its own scroller and sizes itself to the screen — `Sheet` — so nothing can scroll the
   * layer: WebKit scrolled it to reveal the focused field as the keyboard shortened the page, and
   * lifted the whole sheet, its header under the status bar (I-1215, 7–22 px, measured).
   */
  layerScrolls?: boolean;
  testId?: string;
  /** `data-*` attributes for the dialog element, keyed without the prefix (`{ intent: 'cancel' }`). */
  data?: Record<string, string | undefined>;
  /**
   * I-1262 (AC-18.10): a drawer's enter and exit (`useDrawerMotion`) — the backdrop's and the
   * panel's styles, on one timing. Both are marked `data-motion="fade"`, so Reduce Motion keeps
   * their fade (`index.css`).
   */
  motion?: { backdrop: CSSProperties; panel: CSSProperties };
  children: ReactNode;
}) {
  const ownPanelRef = useRef<HTMLDivElement>(null);
  const panelRef = givenPanelRef ?? ownPanelRef;
  useDialogFocusTrap(panelRef, onClose, initialFocusRef);
  const edges = useOverlayEdges();
  return (
    <div
      className={`fixed inset-0 ${
        // `clip`, not `hidden`: an `overflow: hidden` box can still be scrolled by script and by
        // the browser revealing a focused field; a clipped one cannot be scrolled at all.
        layerScrolls ? 'overflow-y-auto overscroll-contain' : 'overflow-clip'
      } ${layerClassName}`}
      // Below the status bar, which the app's web view runs under: a dialog taller than the screen
      // started 16 from its top edge, under the clock (I-1163). And above the home indicator, or
      // the tab bar where it stays: its last action sat under it (I-1209) — except with the
      // keyboard up, which nothing rests under. The backdrop still covers it all. Both edges, and the
      // room between them (`--modal-room`), are the one rule every overlay reads (`overlayEdges.ts`).
      style={belowStatusBar ? edges : ({ '--modal-room': '100dvh' } as CSSProperties)}
    >
      <div
        aria-hidden="true"
        className={`fixed inset-0 ${backdropClassName}`}
        onClick={closeOnBackdrop ? onClose : undefined}
        style={motion?.backdrop}
        data-modal-backdrop=""
        data-motion={motion ? 'fade' : undefined}
      />
      <div
        ref={panelRef}
        role={role}
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-label={labelledBy ? undefined : label}
        aria-describedby={describedBy}
        tabIndex={-1}
        // A modal owns the keyboard: no key typed in it reaches the page's shortcuts behind it.
        // Measured (I-0383): Enter on a confirmation's button also reached the item list's Enter
        // shortcut, which unmounted the dialog before the button could act. Escape is handled by
        // the focus trap on this same element, which runs first.
        onKeyDown={(event) => event.stopPropagation()}
        data-testid={testId}
        {...Object.fromEntries(
          Object.entries(data ?? {}).map(([name, value]) => [`data-${name}`, value]),
        )}
        // I-0995: a name can be one unbreakable run (`org_1790782513581/Alpha_1790782513581`, an
        // e-mail, a key), and it used to set the panel's width: 464 px in a 390 px view. `min-w-0`
        // lets a grid layer's `auto` column shrink the panel to the view, so a `truncate` inside is
        // cut short; `overflow-wrap: anywhere` breaks such a run where it must, as text. Measured
        // at 390 and at the largest text size in WebKit; `docs/concept-page-layout.md`, I-0995.
        data-modal-panel=""
        data-motion={motion ? 'fade' : undefined}
        style={motion?.panel}
        className={`${panelClassName} my-auto min-w-0 outline-none [overflow-wrap:anywhere]`}
      >
        {children}
      </div>
    </div>
  );
}
